import { useState } from "react";
import { useI18n } from "@/i18n";
import { exportMenuPdf } from "@/api/endpoints";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { FileDown, Crown } from "lucide-react";

interface PdfExportDialogProps {
  open: boolean;
  onClose: () => void;
  venueId: string;
  menuId: string;
  primaryColor: string;
}

export function PdfExportDialog({ open, onClose, venueId, menuId, primaryColor }: PdfExportDialogProps) {
  const { t, lang } = useI18n();
  const [exporting, setExporting] = useState(false);

  const [logoPosition, setLogoPosition] = useState<"left" | "center" | "hidden">("left");
  const [categoryBg, setCategoryBg] = useState(primaryColor);
  const [categoryTextColor, setCategoryTextColor] = useState("#ffffff");
  const [titleColor, setTitleColor] = useState(primaryColor);
  const [priceColor, setPriceColor] = useState(primaryColor);
  const [bgColor, setBgColor] = useState("#FFFFFF");
  const [itemTextColor, setItemTextColor] = useState("#1f2937");
  const [showDescriptions, setShowDescriptions] = useState(true);
  const [showBadges, setShowBadges] = useState(true);
  const [showVariants, setShowVariants] = useState(true);
  const [fontSize, setFontSize] = useState<"small" | "medium" | "large">("medium");

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportMenuPdf(venueId, menuId, {
        lang,
        logoPosition,
        categoryBg,
        categoryTextColor,
        titleColor,
        priceColor,
        bgColor,
        itemTextColor,
        showDescriptions,
        showBadges,
        showVariants,
        fontSize,
      });
      const url = URL.createObjectURL(blob as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "menu.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  // Mock preview
  const previewBgDark = bgColor !== "#FFFFFF" && bgColor !== "#ffffff";
  const previewText = previewBgDark ? "#f1f5f9" : itemTextColor;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="size-5" />
            PDF Export
            <Badge variant="secondary" className="ml-2"><Crown className="mr-1 size-3" />Premium</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="flex gap-6">
            {/* Settings */}
            <div className="flex-1 space-y-4">
              {/* Logo */}
              <div>
                <Label>Logo</Label>
                <div className="flex gap-2 mt-1">
                  {(["left", "center", "hidden"] as const).map((pos) => (
                    <Badge
                      key={pos}
                      variant={logoPosition === pos ? "default" : "outline"}
                      className="cursor-pointer capitalize"
                      onClick={() => setLogoPosition(pos)}
                    >
                      {pos === "left" ? t("list") : pos === "center" ? "Center" : "Hidden"}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Colors */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("bg_color")}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="size-8 cursor-pointer rounded border" />
                    <span className="text-xs text-muted-foreground">{bgColor}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t("primary_color")}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={titleColor} onChange={(e) => { setTitleColor(e.target.value); setCategoryBg(e.target.value); setPriceColor(e.target.value); }} className="size-8 cursor-pointer rounded border" />
                    <span className="text-xs text-muted-foreground">{titleColor}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Kategori Arkaplan</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={categoryBg} onChange={(e) => setCategoryBg(e.target.value)} className="size-8 cursor-pointer rounded border" />
                    <span className="text-xs text-muted-foreground">{categoryBg}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Kategori Yazi</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={categoryTextColor} onChange={(e) => setCategoryTextColor(e.target.value)} className="size-8 cursor-pointer rounded border" />
                    <span className="text-xs text-muted-foreground">{categoryTextColor}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Urun Yazi</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={itemTextColor} onChange={(e) => setItemTextColor(e.target.value)} className="size-8 cursor-pointer rounded border" />
                    <span className="text-xs text-muted-foreground">{itemTextColor}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t("price")}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={priceColor} onChange={(e) => setPriceColor(e.target.value)} className="size-8 cursor-pointer rounded border" />
                    <span className="text-xs text-muted-foreground">{priceColor}</span>
                  </div>
                </div>
              </div>

              {/* Font size */}
              <div>
                <Label className="text-xs">Font</Label>
                <div className="flex gap-2 mt-1">
                  {(["small", "medium", "large"] as const).map((s) => (
                    <Badge key={s} variant={fontSize === s ? "default" : "outline"} className="cursor-pointer capitalize" onClick={() => setFontSize(s)}>
                      {s === "small" ? "S" : s === "medium" ? "M" : "L"}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Switch checked={showDescriptions} onCheckedChange={setShowDescriptions} />
                  <Label className="text-xs">{t("description")}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={showBadges} onCheckedChange={setShowBadges} />
                  <Label className="text-xs">{t("tags")}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={showVariants} onCheckedChange={setShowVariants} />
                  <Label className="text-xs">Varyantlar</Label>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="hidden sm:block w-56">
              <p className="mb-2 text-xs text-muted-foreground">{t("live_preview")}</p>
              <div
                className="rounded-lg border shadow-sm overflow-hidden"
                style={{ background: bgColor, padding: 16, minHeight: 340, fontSize: fontSize === "small" ? 9 : fontSize === "large" ? 13 : 11 }}
              >
                {/* Mock logo */}
                {logoPosition !== "hidden" && (
                  <div style={{
                    width: 24, height: 24, borderRadius: 6, background: titleColor,
                    margin: logoPosition === "center" ? "0 auto 8px" : "0 0 8px 0",
                  }} />
                )}
                {/* Title */}
                <div style={{ color: titleColor, fontWeight: 700, fontSize: "1.2em", textAlign: "center" }}>Venue</div>
                <div style={{ color: previewBgDark ? "#94a3b8" : "#9ca3af", fontSize: "0.8em", textAlign: "center", marginBottom: 8 }}>Menu</div>
                {/* Category */}
                <div style={{ background: categoryBg, color: categoryTextColor, padding: "4px 8px", borderRadius: 4, fontWeight: 600, fontSize: "0.85em", marginBottom: 6 }}>
                  KATEGORI
                </div>
                {/* Items */}
                {["Urun 1", "Urun 2", "Urun 3"].map((name, i) => (
                  <div key={i} style={{ borderBottom: `1px solid ${previewBgDark ? "#333" : "#e5e7eb"}`, padding: "4px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: previewText, fontWeight: 500 }}>{name}</span>
                      <span style={{ color: priceColor, fontWeight: 600 }}>99.00</span>
                    </div>
                    {showDescriptions && (
                      <div style={{ color: previewBgDark ? "#64748b" : "#9ca3af", fontSize: "0.8em" }}>Aciklama</div>
                    )}
                    {showBadges && i === 0 && (
                      <div style={{ color: titleColor, fontSize: "0.7em" }}>vegan · popular</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={handleExport} disabled={exporting}>
            <FileDown className="mr-2 size-4" />
            {exporting ? t("loading") : "PDF"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
